import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { scheduledEmailTypeAPI, emailTemplateAPI, seasonEventAPI } from '../services/api';

/**
 * ScheduledEmailEdit - Notification-centric edit page
 *
 * This is the primary interface for viewing, editing, testing, and managing
 * individual scheduled notifications. Accessed via /scheduled-emails/edit/:notificationId
 *
 * Features:
 * - View notification details and status
 * - Edit schedule (if pending)
 * - Preview email content
 * - Send test emails
 * - Skip/Unskip notifications
 * - Send immediately
 * - Retry failed notifications
 * - View override status
 */

const statusColors = {
  pending: { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-200' },
  processing: { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-200' },
  sent: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-200' },
  failed: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-200' },
  skipped: { bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-200' },
  cancelled: { bg: 'bg-gray-100', text: 'text-gray-500', border: 'border-gray-200' }
};

const statusIcons = {
  pending: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  processing: (
    <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
  ),
  sent: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  ),
  failed: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  ),
  skipped: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
    </svg>
  ),
  cancelled: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  )
};

const ScheduledEmailEdit = () => {
  const navigate = useNavigate();
  const { notificationId } = useParams();

  const [notification, setNotification] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [activeTab, setActiveTab] = useState('details');

  // Form state for editing
  const [formData, setFormData] = useState({
    scheduledDate: '',
    scheduledTime: '08:00',
    subject: '',
    recipientOverride: {
      hasOverride: false,
      toAddresses: [],
      ccAddresses: [],
      bccAddresses: [],
      additionalRecipients: [],
      excludedEmails: [],
      includeProshopContact: undefined,
      includeTournamentPlayers: undefined,
      includeWaitlist: undefined,
      includeAdminContact: undefined
    }
  });

  // Preview state
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Test email state
  const [showTestEmailModal, setShowTestEmailModal] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [testEmailResult, setTestEmailResult] = useState(null);

  // Skip modal state
  const [showSkipModal, setShowSkipModal] = useState(false);
  const [skipReason, setSkipReason] = useState('');

  useEffect(() => {
    if (notificationId) {
      fetchData();
    }
  }, [notificationId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [notifRes, templatesRes] = await Promise.all([
        scheduledEmailTypeAPI.getNotificationById(notificationId),
        emailTemplateAPI.getAll({ active: true })
      ]);

      const notifData = notifRes.data.notification || notifRes.data;
      setNotification(notifData);

      // Ensure templates is always an array
      const templateData = templatesRes.data;
      let templateArray = [];
      if (Array.isArray(templateData)) {
        templateArray = templateData;
      } else if (templateData?.templates && Array.isArray(templateData.templates)) {
        templateArray = templateData.templates;
      } else if (templateData?.data && Array.isArray(templateData.data)) {
        templateArray = templateData.data;
      }
      setTemplates(templateArray);

      // Initialize form with current values
      if (notifData?.scheduledFor) {
        const date = new Date(notifData.scheduledFor);
        setFormData({
          scheduledDate: date.toISOString().split('T')[0],
          scheduledTime: date.toTimeString().slice(0, 5),
          subject: notifData.emailDetails?.subject || '',
          recipientOverride: {
            hasOverride: notifData.recipientOverride?.hasOverride || false,
            toAddresses: notifData.recipientOverride?.toAddresses || [],
            ccAddresses: notifData.recipientOverride?.ccAddresses || [],
            bccAddresses: notifData.recipientOverride?.bccAddresses || [],
            additionalRecipients: notifData.recipientOverride?.additionalRecipients || [],
            excludedEmails: notifData.recipientOverride?.excludedEmails || [],
            includeProshopContact: notifData.recipientOverride?.includeProshopContact,
            includeTournamentPlayers: notifData.recipientOverride?.includeTournamentPlayers,
            includeWaitlist: notifData.recipientOverride?.includeWaitlist,
            includeAdminContact: notifData.recipientOverride?.includeAdminContact
          }
        });
      }
    } catch (err) {
      console.error('Error fetching notification:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load notification');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      // Build the update payload
      const scheduledDateTime = new Date(`${formData.scheduledDate}T${formData.scheduledTime}`);

      const updatePayload = {
        scheduledFor: scheduledDateTime.toISOString()
      };

      // If subject was changed, include it
      if (formData.subject && formData.subject !== notification.emailDetails?.subject) {
        updatePayload.emailDetails = {
          ...notification.emailDetails,
          subject: formData.subject
        };
      }

      // Include recipient override if it has been modified
      if (formData.recipientOverride) {
        // Filter out empty addresses before saving
        const cleanedRecipientOverride = {
          ...formData.recipientOverride,
          toAddresses: (formData.recipientOverride.toAddresses || []).filter(a => a.email?.trim()),
          ccAddresses: (formData.recipientOverride.ccAddresses || []).filter(a => a.email?.trim()),
          bccAddresses: (formData.recipientOverride.bccAddresses || []).filter(a => a.email?.trim()),
          overriddenAt: formData.recipientOverride.hasOverride ? new Date().toISOString() : undefined,
          overriddenBy: formData.recipientOverride.hasOverride ? 'admin' : undefined
        };
        updatePayload.recipientOverride = cleanedRecipientOverride;
      }

      await scheduledEmailTypeAPI.updateNotification(notificationId, updatePayload);

      setSuccessMessage('Changes saved successfully');
      await fetchData(); // Refresh the data

      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Error saving:', err);
      setError(err.response?.data?.error || err.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    try {
      setLoadingPreview(true);
      setError(null);

      // Use the notification ID to get preview that respects template overrides
      const response = await scheduledEmailTypeAPI.previewScheduledNotification(notificationId);
      setPreview(response.data.preview);
    } catch (err) {
      console.error('Error loading preview:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load preview');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleSendTest = async () => {
    if (!testEmail || !testEmail.includes('@')) {
      setTestEmailResult({ success: false, message: 'Please enter a valid email address' });
      return;
    }

    try {
      setSendingTest(true);
      setTestEmailResult(null);

      const eventId = notification.seasonEventId?._id || notification.seasonEventId;
      const notificationType = notification.emailTypeCode || notification.notificationType;
      const templateName = notification.emailDetails?.templateName;

      const response = await seasonEventAPI.testNotification(eventId, notificationType, testEmail, templateName);
      setTestEmailResult({
        success: true,
        message: response.data.message || `Test email sent to ${testEmail}`
      });
      setTimeout(() => {
        setShowTestEmailModal(false);
        setTestEmailResult(null);
      }, 3000);
    } catch (err) {
      console.error('Error sending test email:', err);
      setTestEmailResult({
        success: false,
        message: err.response?.data?.error || err.message || 'Failed to send test email'
      });
    } finally {
      setSendingTest(false);
    }
  };

  const handleSendNow = async () => {
    if (!window.confirm('Are you sure you want to send this notification now? This action cannot be undone.')) {
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const eventId = notification.seasonEventId?._id || notification.seasonEventId;
      const notificationType = notification.emailTypeCode || notification.notificationType;

      await seasonEventAPI.triggerNotification(eventId, notificationType);

      setSuccessMessage('Notification sent successfully!');
      await fetchData(); // Refresh the data
    } catch (err) {
      console.error('Error triggering notification:', err);
      setError(err.response?.data?.error || err.message || 'Failed to send notification');
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = async () => {
    try {
      setSaving(true);
      setError(null);

      await scheduledEmailTypeAPI.skipNotification(notificationId, skipReason || 'Manually skipped', 'admin');

      setSuccessMessage('Notification skipped');
      setShowSkipModal(false);
      setSkipReason('');
      await fetchData();
    } catch (err) {
      console.error('Error skipping notification:', err);
      setError(err.response?.data?.error || err.message || 'Failed to skip notification');
    } finally {
      setSaving(false);
    }
  };

  const handleUnskip = async () => {
    try {
      setSaving(true);
      setError(null);

      await scheduledEmailTypeAPI.unskipNotification(notificationId, 'admin');

      setSuccessMessage('Notification re-enabled');
      await fetchData();
    } catch (err) {
      console.error('Error unskipping notification:', err);
      setError(err.response?.data?.error || err.message || 'Failed to unskip notification');
    } finally {
      setSaving(false);
    }
  };

  const handleRetry = async () => {
    if (!window.confirm('Retry sending this notification?')) {
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const eventId = notification.seasonEventId?._id || notification.seasonEventId;
      const notificationType = notification.emailTypeCode || notification.notificationType;

      await seasonEventAPI.triggerNotification(eventId, notificationType);

      setSuccessMessage('Retry initiated');
      await fetchData();
    } catch (err) {
      console.error('Error retrying notification:', err);
      setError(err.response?.data?.error || err.message || 'Failed to retry');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete this notification? This action cannot be undone.`)) {
      return;
    }

    try {
      setSaving(true);
      setError(null);

      await scheduledEmailTypeAPI.deleteNotification(notificationId);

      setSuccessMessage('Notification deleted');
      setTimeout(() => {
        navigate('/scheduled-emails?tab=list');
      }, 1000);
    } catch (err) {
      console.error('Error deleting notification:', err);
      setError(err.response?.data?.error || err.message || 'Failed to delete notification');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/scheduled-emails');
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDateTime = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('en-US', {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  };

  // Get event data from populated seasonEventId
  const event = notification?.seasonEventId;
  const eventName = typeof event === 'object' ? event?.name : notification?.eventName;
  const eventDate = typeof event === 'object' ? event?.eventDate : notification?.eventDate;

  const status = notification?.status || 'pending';
  const statusStyle = statusColors[status] || statusColors.pending;
  const canEdit = status === 'pending';
  const canSkip = status === 'pending';
  const canUnskip = status === 'skipped' && new Date(notification?.scheduledFor) > new Date();
  const canRetry = status === 'failed';
  const canSendNow = status === 'pending' || status === 'failed';
  const canDelete = ['pending', 'skipped', 'failed'].includes(status);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-semibold text-slate-900">
                Scheduled Email Details
              </h1>
              {eventName && (
                <p className="text-sm text-slate-600 mt-1">{eventName}</p>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancel}
            >
              Back to Schedule
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="bg-white rounded-lg border shadow-sm p-12 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3" />
            <p className="text-slate-500">Loading notification...</p>
          </div>
        ) : error && !notification ? (
          <div className="bg-white rounded-lg border shadow-sm p-6">
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
              {error}
            </div>
            <Button
              variant="outline"
              className="mt-4"
              onClick={handleCancel}
            >
              Back to Schedule
            </Button>
          </div>
        ) : notification ? (
          <div className="space-y-6">
            {/* Status Header Card */}
            <div className="bg-white rounded-lg border shadow-sm p-6">
              <div className="flex items-start justify-between flex-wrap gap-4">
                <div className="space-y-2">
                  {/* Email Type Badge */}
                  <Badge className="bg-blue-600 text-white">
                    {notification.emailTypeCode || notification.notificationType || 'Notification'}
                  </Badge>

                  {/* Status Badge */}
                  <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${statusStyle.bg} ${statusStyle.text} ${statusStyle.border} border ml-2`}>
                    {statusIcons[status]}
                    <span className="capitalize">{notification.statusDisplay || status}</span>
                  </div>

                  {/* Override indicator */}
                  {notification.hasOverride && (
                    <Badge variant="outline" className="ml-2 bg-amber-50 text-amber-700 border-amber-200">
                      Has Overrides
                    </Badge>
                  )}
                </div>

                {/* Quick Actions */}
                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowTestEmailModal(true)}
                    className="flex items-center gap-1"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Test
                  </Button>

                  {canSkip && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowSkipModal(true)}
                      className="text-orange-600 border-orange-300 hover:bg-orange-50"
                    >
                      Skip
                    </Button>
                  )}

                  {canUnskip && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleUnskip}
                      disabled={saving}
                      className="text-green-600 border-green-300 hover:bg-green-50"
                    >
                      Re-enable
                    </Button>
                  )}

                  {canRetry && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleRetry}
                      disabled={saving}
                      className="text-orange-600 border-orange-300 hover:bg-orange-50"
                    >
                      Retry
                    </Button>
                  )}

                  {canSendNow && (
                    <Button
                      size="sm"
                      onClick={handleSendNow}
                      disabled={saving}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      {saving ? 'Sending...' : 'Send Now'}
                    </Button>
                  )}

                  {canDelete && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleDelete}
                      disabled={saving}
                      className="text-red-600 border-red-300 hover:bg-red-50"
                    >
                      Delete
                    </Button>
                  )}
                </div>
              </div>

              {/* Key Info Row */}
              <div className="mt-4 pt-4 border-t grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-slate-500">Scheduled For</span>
                  <p className="font-medium mt-1">{formatDateTime(notification.scheduledFor)}</p>
                </div>
                <div>
                  <span className="text-slate-500">Event Date</span>
                  <p className="font-medium mt-1">{formatDate(eventDate)}</p>
                </div>
                <div>
                  <span className="text-slate-500">Recipients</span>
                  <p className="font-medium mt-1">{notification.recipientCount || notification.recipients?.length || 0}</p>
                </div>
                <div>
                  <span className="text-slate-500">Retries</span>
                  <p className="font-medium mt-1">{notification.retryCount || 0} / {notification.maxRetries || 3}</p>
                </div>
              </div>
            </div>

            {/* Messages */}
            {successMessage && (
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md text-sm">
                {successMessage}
              </div>
            )}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                {error}
              </div>
            )}

            {/* Tabs */}
            <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
              <div className="flex border-b px-6">
                <button
                  onClick={() => setActiveTab('details')}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'details'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Details
                </button>
                <button
                  onClick={() => setActiveTab('edit')}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'edit'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Edit
                </button>
                <button
                  onClick={() => { setActiveTab('preview'); handlePreview(); }}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'preview'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Preview
                </button>
                <button
                  onClick={() => setActiveTab('recipients')}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'recipients'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Recipients
                  {formData.recipientOverride?.hasOverride && (
                    <span className="ml-1 text-amber-500">•</span>
                  )}
                </button>
                <button
                  onClick={() => setActiveTab('history')}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'history'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-slate-600 hover:text-slate-900'
                  }`}
                >
                  History
                </button>
              </div>

              {/* Tab Content */}
              <div className="p-6">
                {/* Details Tab */}
                {activeTab === 'details' && (
                  <div className="space-y-6">
                    {/* Email Details */}
                    <div>
                      <h3 className="text-sm font-medium text-slate-700 mb-3">Email Content</h3>
                      <dl className="space-y-3">
                        <div className="bg-slate-50 rounded-lg p-4">
                          <dt className="text-xs font-medium text-slate-500 uppercase">Subject</dt>
                          <dd className="mt-1 font-medium">{notification.emailDetails?.subject || 'Default subject'}</dd>
                        </div>
                        {notification.emailDetails?.bodyPreview && (
                          <div className="bg-slate-50 rounded-lg p-4">
                            <dt className="text-xs font-medium text-slate-500 uppercase">Body Preview</dt>
                            <dd className="mt-1 text-sm text-slate-600">{notification.emailDetails.bodyPreview}</dd>
                          </div>
                        )}
                        {notification.emailDetails?.templateName && (
                          <div>
                            <dt className="text-slate-500 text-sm">Template</dt>
                            <dd className="font-medium">{notification.emailDetails.templateName}</dd>
                          </div>
                        )}
                      </dl>
                    </div>

                    {/* Recipients */}
                    {notification.recipients && notification.recipients.length > 0 && (
                      <div>
                        <h3 className="text-sm font-medium text-slate-700 mb-3">Recipients</h3>
                        <div className="bg-slate-50 rounded-lg p-4">
                          <ul className="space-y-2">
                            {notification.recipients.map((r, i) => (
                              <li key={i} className="flex items-center gap-2 text-sm">
                                <Badge variant="outline" className="text-xs">{r.type}</Badge>
                                <span>{r.name || r.email}</span>
                                {r.name && <span className="text-slate-500">({r.email})</span>}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}

                    {/* Override Info */}
                    {notification.hasOverride && (
                      <div>
                        <h3 className="text-sm font-medium text-amber-700 mb-3">Override Information</h3>
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                          <p className="text-sm text-amber-700 mb-2">
                            This notification has custom overrides and won't be updated when the email type changes.
                          </p>
                          {notification.overriddenFields?.length > 0 && (
                            <div className="mt-2">
                              <span className="text-xs font-medium text-amber-600">Overridden fields:</span>
                              <div className="flex gap-2 mt-1 flex-wrap">
                                {notification.overriddenFields.map((field, i) => (
                                  <Badge key={i} variant="outline" className="text-amber-700 border-amber-300">
                                    {field}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                          {notification.overriddenBy && (
                            <p className="text-xs text-amber-600 mt-2">
                              Overridden by {notification.overriddenBy} on {formatDateTime(notification.overriddenAt)}
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Error Info */}
                    {status === 'failed' && notification.error && (
                      <div>
                        <h3 className="text-sm font-medium text-red-700 mb-3">Error Details</h3>
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                          <p className="text-red-700 font-medium">{notification.error.message}</p>
                          {notification.error.code && (
                            <p className="text-sm text-red-600 mt-1">Code: {notification.error.code}</p>
                          )}
                          {notification.error.occurredAt && (
                            <p className="text-xs text-red-500 mt-2">
                              Occurred at: {formatDateTime(notification.error.occurredAt)}
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Skip Info */}
                    {status === 'skipped' && notification.manualOverride?.skipped && (
                      <div>
                        <h3 className="text-sm font-medium text-slate-700 mb-3">Skip Information</h3>
                        <div className="bg-slate-100 rounded-lg p-4">
                          {notification.manualOverride.skippedReason && (
                            <p className="text-slate-600 italic">"{notification.manualOverride.skippedReason}"</p>
                          )}
                          <p className="text-xs text-slate-500 mt-2">
                            Skipped by {notification.manualOverride.skippedBy || 'admin'} on{' '}
                            {formatDateTime(notification.manualOverride.skippedAt)}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Delivery Results */}
                    {status === 'sent' && notification.deliveryResults && (
                      <div>
                        <h3 className="text-sm font-medium text-green-700 mb-3">Delivery Results</h3>
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="text-green-600">Sent:</span>
                              <span className="font-medium ml-2">{notification.deliveryResults.sent || 0}</span>
                            </div>
                            <div>
                              <span className="text-green-600">Failed:</span>
                              <span className="font-medium ml-2">{notification.deliveryResults.failed || 0}</span>
                            </div>
                          </div>
                          {notification.completedAt && (
                            <p className="text-xs text-green-600 mt-3">
                              Completed at: {formatDateTime(notification.completedAt)}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Edit Tab */}
                {activeTab === 'edit' && (
                  <div className="space-y-6">
                    {!canEdit ? (
                      <div className="bg-slate-50 rounded-lg p-6 text-center text-slate-500">
                        <p>This notification cannot be edited because it has already been {status}.</p>
                      </div>
                    ) : (
                      <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">
                              Scheduled Date
                            </label>
                            <input
                              type="date"
                              value={formData.scheduledDate}
                              onChange={(e) => handleInputChange('scheduledDate', e.target.value)}
                              className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">
                              Scheduled Time
                            </label>
                            <input
                              type="time"
                              value={formData.scheduledTime}
                              onChange={(e) => handleInputChange('scheduledTime', e.target.value)}
                              className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                            <p className="text-xs text-slate-500 mt-1">
                              Automated emails are checked at 8:00 AM and 6:00 PM Pacific
                            </p>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            Subject Line
                          </label>
                          <input
                            type="text"
                            value={formData.subject}
                            onChange={(e) => handleInputChange('subject', e.target.value)}
                            placeholder="Email subject"
                            className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                          <p className="text-xs text-slate-500 mt-1">
                            Editing this will create an override that won't be affected by email type changes
                          </p>
                        </div>

                        <div className="flex justify-end gap-3 pt-4 border-t">
                          <Button
                            variant="outline"
                            onClick={() => {
                              // Reset form to original values
                              if (notification?.scheduledFor) {
                                const date = new Date(notification.scheduledFor);
                                setFormData({
                                  scheduledDate: date.toISOString().split('T')[0],
                                  scheduledTime: date.toTimeString().slice(0, 5),
                                  subject: notification.emailDetails?.subject || ''
                                });
                              }
                            }}
                          >
                            Reset
                          </Button>
                          <Button
                            onClick={handleSave}
                            disabled={saving}
                          >
                            {saving ? 'Saving...' : 'Save Changes'}
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Preview Tab */}
                {activeTab === 'preview' && (
                  <div className="space-y-6">
                    {loadingPreview ? (
                      <div className="text-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3" />
                        <p className="text-slate-500">Loading preview...</p>
                      </div>
                    ) : preview ? (
                      <>
                        <div className="bg-slate-50 rounded-lg p-4">
                          <h4 className="text-xs font-medium text-slate-500 uppercase mb-1">Subject</h4>
                          <p className="font-medium">{preview.subject}</p>
                        </div>

                        <div className="bg-slate-50 rounded-lg p-4">
                          <h4 className="text-xs font-medium text-slate-500 uppercase mb-1">Recipients</h4>
                          {preview.resolvedRecipients ? (
                            <div className="space-y-1 text-sm">
                              {preview.resolvedRecipients.to?.length > 0 && (
                                <div className="flex gap-2">
                                  <span className="text-slate-500 font-medium w-10">To:</span>
                                  <span>{preview.resolvedRecipients.to.map(r => r.name ? `${r.name} <${r.email}>` : r.email).join(', ')}</span>
                                </div>
                              )}
                              {preview.resolvedRecipients.cc?.length > 0 && (
                                <div className="flex gap-2">
                                  <span className="text-slate-500 font-medium w-10">CC:</span>
                                  <span>{preview.resolvedRecipients.cc.map(r => r.name ? `${r.name} <${r.email}>` : r.email).join(', ')}</span>
                                </div>
                              )}
                              {preview.resolvedRecipients.bcc?.length > 0 && (
                                <div className="flex gap-2">
                                  <span className="text-slate-500 font-medium w-10">BCC:</span>
                                  <span>{preview.resolvedRecipients.bcc.map(r => r.name ? `${r.name} <${r.email}>` : r.email).join(', ')}</span>
                                </div>
                              )}
                              {preview.isDefaultRecipients && (
                                <span className="text-xs text-slate-400">(default)</span>
                              )}
                              {preview.hasRecipientOverride && (
                                <span className="text-xs text-blue-500">(override active)</span>
                              )}
                            </div>
                          ) : (
                            <p className="text-sm">
                              {preview.recipients?.join(', ') || preview.recipient || 'No recipients'}
                              {preview.isDefaultRecipients && (
                                <span className="ml-2 text-xs text-slate-400">(default)</span>
                              )}
                            </p>
                          )}
                        </div>

                        <div className="border rounded-lg overflow-hidden">
                          <div className="bg-slate-100 px-4 py-2 border-b">
                            <span className="text-xs font-medium text-slate-500 uppercase">Email Body</span>
                          </div>
                          <div
                            className="p-4 bg-white max-h-96 overflow-y-auto"
                            dangerouslySetInnerHTML={{ __html: preview.body || '<p>No preview available</p>' }}
                          />
                        </div>

                        {/* Refresh Preview Button */}
                        <div className="flex items-center gap-3 pt-4 border-t">
                          <Button
                            variant="outline"
                            onClick={handlePreview}
                            size="sm"
                          >
                            Refresh Preview
                          </Button>
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-12 text-slate-500">
                        <p>Click to load email preview</p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handlePreview}
                          className="mt-2"
                        >
                          Load Preview
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* Recipients Tab */}
                {activeTab === 'recipients' && (
                  <div className="space-y-6">
                    {!canEdit ? (
                      <div className="bg-slate-50 rounded-lg p-6 text-center text-slate-500">
                        <p>Recipients cannot be modified because this notification has already been {status}.</p>
                      </div>
                    ) : (
                      <>
                        {/* Override Toggle */}
                        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                          <div>
                            <h3 className="font-medium text-slate-900">Override Recipients</h3>
                            <p className="text-sm text-slate-600">
                              Customize recipients for this specific notification instead of using the email type defaults
                            </p>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.recipientOverride?.hasOverride || false}
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                recipientOverride: {
                                  ...prev.recipientOverride,
                                  hasOverride: e.target.checked
                                }
                              }))}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                          </label>
                        </div>

                        {formData.recipientOverride?.hasOverride && (
                          <>
                            {/* To Addresses */}
                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-2">
                                To Recipients
                              </label>
                              <div className="space-y-2">
                                {formData.recipientOverride.toAddresses?.map((addr, index) => (
                                  <div key={index} className="flex items-center gap-2">
                                    <input
                                      type="text"
                                      value={addr.name || ''}
                                      onChange={(e) => {
                                        const newAddresses = [...formData.recipientOverride.toAddresses];
                                        newAddresses[index] = { ...newAddresses[index], name: e.target.value };
                                        setFormData(prev => ({
                                          ...prev,
                                          recipientOverride: { ...prev.recipientOverride, toAddresses: newAddresses }
                                        }));
                                      }}
                                      placeholder="Name"
                                      className="w-1/3 px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                    <input
                                      type="email"
                                      value={addr.email || ''}
                                      onChange={(e) => {
                                        const newAddresses = [...formData.recipientOverride.toAddresses];
                                        newAddresses[index] = { ...newAddresses[index], email: e.target.value };
                                        setFormData(prev => ({
                                          ...prev,
                                          recipientOverride: { ...prev.recipientOverride, toAddresses: newAddresses }
                                        }));
                                      }}
                                      placeholder="email@example.com"
                                      className="flex-1 px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => {
                                        const newAddresses = formData.recipientOverride.toAddresses.filter((_, i) => i !== index);
                                        setFormData(prev => ({
                                          ...prev,
                                          recipientOverride: { ...prev.recipientOverride, toAddresses: newAddresses }
                                        }));
                                      }}
                                      className="text-red-500 hover:text-red-700"
                                    >
                                      ×
                                    </Button>
                                  </div>
                                ))}
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setFormData(prev => ({
                                      ...prev,
                                      recipientOverride: {
                                        ...prev.recipientOverride,
                                        toAddresses: [...(prev.recipientOverride.toAddresses || []), { email: '', name: '' }]
                                      }
                                    }));
                                  }}
                                  className="text-xs"
                                >
                                  + Add To Recipient
                                </Button>
                              </div>
                            </div>

                            {/* CC Addresses */}
                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-2">
                                CC Recipients
                              </label>
                              <div className="space-y-2">
                                {formData.recipientOverride.ccAddresses?.map((addr, index) => (
                                  <div key={index} className="flex items-center gap-2">
                                    <input
                                      type="text"
                                      value={addr.name || ''}
                                      onChange={(e) => {
                                        const newAddresses = [...formData.recipientOverride.ccAddresses];
                                        newAddresses[index] = { ...newAddresses[index], name: e.target.value };
                                        setFormData(prev => ({
                                          ...prev,
                                          recipientOverride: { ...prev.recipientOverride, ccAddresses: newAddresses }
                                        }));
                                      }}
                                      placeholder="Name"
                                      className="w-1/3 px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                    <input
                                      type="email"
                                      value={addr.email || ''}
                                      onChange={(e) => {
                                        const newAddresses = [...formData.recipientOverride.ccAddresses];
                                        newAddresses[index] = { ...newAddresses[index], email: e.target.value };
                                        setFormData(prev => ({
                                          ...prev,
                                          recipientOverride: { ...prev.recipientOverride, ccAddresses: newAddresses }
                                        }));
                                      }}
                                      placeholder="email@example.com"
                                      className="flex-1 px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => {
                                        const newAddresses = formData.recipientOverride.ccAddresses.filter((_, i) => i !== index);
                                        setFormData(prev => ({
                                          ...prev,
                                          recipientOverride: { ...prev.recipientOverride, ccAddresses: newAddresses }
                                        }));
                                      }}
                                      className="text-red-500 hover:text-red-700"
                                    >
                                      ×
                                    </Button>
                                  </div>
                                ))}
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setFormData(prev => ({
                                      ...prev,
                                      recipientOverride: {
                                        ...prev.recipientOverride,
                                        ccAddresses: [...(prev.recipientOverride.ccAddresses || []), { email: '', name: '' }]
                                      }
                                    }));
                                  }}
                                  className="text-xs"
                                >
                                  + Add CC Recipient
                                </Button>
                              </div>
                            </div>

                            {/* BCC Addresses */}
                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-2">
                                BCC Recipients
                              </label>
                              <div className="space-y-2">
                                {formData.recipientOverride.bccAddresses?.map((addr, index) => (
                                  <div key={index} className="flex items-center gap-2">
                                    <input
                                      type="text"
                                      value={addr.name || ''}
                                      onChange={(e) => {
                                        const newAddresses = [...formData.recipientOverride.bccAddresses];
                                        newAddresses[index] = { ...newAddresses[index], name: e.target.value };
                                        setFormData(prev => ({
                                          ...prev,
                                          recipientOverride: { ...prev.recipientOverride, bccAddresses: newAddresses }
                                        }));
                                      }}
                                      placeholder="Name"
                                      className="w-1/3 px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                    <input
                                      type="email"
                                      value={addr.email || ''}
                                      onChange={(e) => {
                                        const newAddresses = [...formData.recipientOverride.bccAddresses];
                                        newAddresses[index] = { ...newAddresses[index], email: e.target.value };
                                        setFormData(prev => ({
                                          ...prev,
                                          recipientOverride: { ...prev.recipientOverride, bccAddresses: newAddresses }
                                        }));
                                      }}
                                      placeholder="email@example.com"
                                      className="flex-1 px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => {
                                        const newAddresses = formData.recipientOverride.bccAddresses.filter((_, i) => i !== index);
                                        setFormData(prev => ({
                                          ...prev,
                                          recipientOverride: { ...prev.recipientOverride, bccAddresses: newAddresses }
                                        }));
                                      }}
                                      className="text-red-500 hover:text-red-700"
                                    >
                                      ×
                                    </Button>
                                  </div>
                                ))}
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setFormData(prev => ({
                                      ...prev,
                                      recipientOverride: {
                                        ...prev.recipientOverride,
                                        bccAddresses: [...(prev.recipientOverride.bccAddresses || []), { email: '', name: '' }]
                                      }
                                    }));
                                  }}
                                  className="text-xs"
                                >
                                  + Add BCC Recipient
                                </Button>
                              </div>
                            </div>

                            {/* Dynamic Options */}
                            <div className="border-t pt-6">
                              <h4 className="text-sm font-medium text-slate-700 mb-3">Dynamic Recipient Options</h4>
                              <p className="text-xs text-slate-500 mb-4">
                                Override the default dynamic recipient settings for this notification.
                                Unchecked = use email type default, Checked = enable, Crossed = disable.
                              </p>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <label className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100">
                                  <input
                                    type="checkbox"
                                    checked={formData.recipientOverride.includeProshopContact === true}
                                    onChange={(e) => setFormData(prev => ({
                                      ...prev,
                                      recipientOverride: {
                                        ...prev.recipientOverride,
                                        includeProshopContact: e.target.checked ? true : undefined
                                      }
                                    }))}
                                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                                  />
                                  <div>
                                    <span className="text-sm font-medium text-slate-700">Proshop Contact</span>
                                    <p className="text-xs text-slate-500">Include proshop email</p>
                                  </div>
                                </label>

                                <label className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100">
                                  <input
                                    type="checkbox"
                                    checked={formData.recipientOverride.includeTournamentPlayers === true}
                                    onChange={(e) => setFormData(prev => ({
                                      ...prev,
                                      recipientOverride: {
                                        ...prev.recipientOverride,
                                        includeTournamentPlayers: e.target.checked ? true : undefined
                                      }
                                    }))}
                                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                                  />
                                  <div>
                                    <span className="text-sm font-medium text-slate-700">Tournament Players</span>
                                    <p className="text-xs text-slate-500">All registered players</p>
                                  </div>
                                </label>

                                <label className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100">
                                  <input
                                    type="checkbox"
                                    checked={formData.recipientOverride.includeWaitlist === true}
                                    onChange={(e) => setFormData(prev => ({
                                      ...prev,
                                      recipientOverride: {
                                        ...prev.recipientOverride,
                                        includeWaitlist: e.target.checked ? true : undefined
                                      }
                                    }))}
                                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                                  />
                                  <div>
                                    <span className="text-sm font-medium text-slate-700">Waitlist</span>
                                    <p className="text-xs text-slate-500">Include waitlisted players</p>
                                  </div>
                                </label>

                                <label className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100">
                                  <input
                                    type="checkbox"
                                    checked={formData.recipientOverride.includeAdminContact === true}
                                    onChange={(e) => setFormData(prev => ({
                                      ...prev,
                                      recipientOverride: {
                                        ...prev.recipientOverride,
                                        includeAdminContact: e.target.checked ? true : undefined
                                      }
                                    }))}
                                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                                  />
                                  <div>
                                    <span className="text-sm font-medium text-slate-700">Admin Contact</span>
                                    <p className="text-xs text-slate-500">Include admin email</p>
                                  </div>
                                </label>
                              </div>
                            </div>

                            {/* Save Button */}
                            <div className="flex justify-end gap-3 pt-4 border-t">
                              <Button
                                variant="outline"
                                onClick={() => {
                                  // Reset to original values
                                  if (notification?.recipientOverride) {
                                    setFormData(prev => ({
                                      ...prev,
                                      recipientOverride: {
                                        hasOverride: notification.recipientOverride.hasOverride || false,
                                        toAddresses: notification.recipientOverride.toAddresses || [],
                                        ccAddresses: notification.recipientOverride.ccAddresses || [],
                                        bccAddresses: notification.recipientOverride.bccAddresses || [],
                                        additionalRecipients: notification.recipientOverride.additionalRecipients || [],
                                        excludedEmails: notification.recipientOverride.excludedEmails || [],
                                        includeProshopContact: notification.recipientOverride.includeProshopContact,
                                        includeTournamentPlayers: notification.recipientOverride.includeTournamentPlayers,
                                        includeWaitlist: notification.recipientOverride.includeWaitlist,
                                        includeAdminContact: notification.recipientOverride.includeAdminContact
                                      }
                                    }));
                                  }
                                }}
                              >
                                Reset
                              </Button>
                              <Button
                                onClick={handleSave}
                                disabled={saving}
                              >
                                {saving ? 'Saving...' : 'Save Recipients'}
                              </Button>
                            </div>
                          </>
                        )}

                        {!formData.recipientOverride?.hasOverride && (
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-700">
                            <p className="font-medium mb-1">Using Email Type Defaults</p>
                            <p>This notification will use the recipient configuration from its email type blueprint. Enable the override toggle above to customize recipients for this specific notification.</p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* History Tab */}
                {activeTab === 'history' && (
                  <div className="space-y-4">
                    <h3 className="text-sm font-medium text-slate-700">Notification Timeline</h3>
                    <div className="border-l-2 border-slate-200 ml-4 space-y-4">
                      {/* Created */}
                      <div className="relative pl-6">
                        <div className="absolute -left-2 w-4 h-4 rounded-full bg-slate-300" />
                        <p className="text-sm font-medium">Created</p>
                        <p className="text-xs text-slate-500">{formatDateTime(notification.createdAt)}</p>
                        <p className="text-xs text-slate-400">by {notification.createdBy || 'system'}</p>
                      </div>

                      {/* Scheduled */}
                      <div className="relative pl-6">
                        <div className="absolute -left-2 w-4 h-4 rounded-full bg-blue-400" />
                        <p className="text-sm font-medium">Scheduled For</p>
                        <p className="text-xs text-slate-500">{formatDateTime(notification.scheduledFor)}</p>
                      </div>

                      {/* Override (if applicable) */}
                      {notification.overriddenAt && (
                        <div className="relative pl-6">
                          <div className="absolute -left-2 w-4 h-4 rounded-full bg-amber-400" />
                          <p className="text-sm font-medium">Overridden</p>
                          <p className="text-xs text-slate-500">{formatDateTime(notification.overriddenAt)}</p>
                          <p className="text-xs text-slate-400">by {notification.overriddenBy}</p>
                        </div>
                      )}

                      {/* Triggered */}
                      {notification.triggeredAt && (
                        <div className="relative pl-6">
                          <div className="absolute -left-2 w-4 h-4 rounded-full bg-purple-400" />
                          <p className="text-sm font-medium">Triggered</p>
                          <p className="text-xs text-slate-500">{formatDateTime(notification.triggeredAt)}</p>
                        </div>
                      )}

                      {/* Skipped */}
                      {notification.manualOverride?.skippedAt && (
                        <div className="relative pl-6">
                          <div className="absolute -left-2 w-4 h-4 rounded-full bg-gray-400" />
                          <p className="text-sm font-medium">Skipped</p>
                          <p className="text-xs text-slate-500">{formatDateTime(notification.manualOverride.skippedAt)}</p>
                          <p className="text-xs text-slate-400">by {notification.manualOverride.skippedBy}</p>
                          {notification.manualOverride.skippedReason && (
                            <p className="text-xs text-slate-500 italic mt-1">"{notification.manualOverride.skippedReason}"</p>
                          )}
                        </div>
                      )}

                      {/* Completed/Failed */}
                      {notification.completedAt && (
                        <div className="relative pl-6">
                          <div className={`absolute -left-2 w-4 h-4 rounded-full ${status === 'sent' ? 'bg-green-500' : 'bg-red-500'}`} />
                          <p className="text-sm font-medium">{status === 'sent' ? 'Sent Successfully' : 'Failed'}</p>
                          <p className="text-xs text-slate-500">{formatDateTime(notification.completedAt)}</p>
                          {notification.deliveryResults && (
                            <p className="text-xs text-slate-400">
                              {notification.deliveryResults.sent} sent, {notification.deliveryResults.failed} failed
                            </p>
                          )}
                        </div>
                      )}

                      {/* Last Retry */}
                      {notification.lastRetryAt && (
                        <div className="relative pl-6">
                          <div className="absolute -left-2 w-4 h-4 rounded-full bg-orange-400" />
                          <p className="text-sm font-medium">Last Retry Attempt</p>
                          <p className="text-xs text-slate-500">{formatDateTime(notification.lastRetryAt)}</p>
                        </div>
                      )}

                      {/* Updated */}
                      {notification.updatedAt && notification.updatedAt !== notification.createdAt && (
                        <div className="relative pl-6">
                          <div className="absolute -left-2 w-4 h-4 rounded-full bg-slate-400" />
                          <p className="text-sm font-medium">Last Updated</p>
                          <p className="text-xs text-slate-500">{formatDateTime(notification.updatedAt)}</p>
                        </div>
                      )}
                    </div>

                    {/* Notes */}
                    {notification.notes && (
                      <div className="mt-6 pt-4 border-t">
                        <h4 className="text-sm font-medium text-slate-700 mb-2">Notes</h4>
                        <p className="text-sm text-slate-600 bg-slate-50 rounded-lg p-3">{notification.notes}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : null}
      </main>

      {/* Test Email Modal */}
      {showTestEmailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold mb-4">Send Test Email</h3>
              <p className="text-sm text-slate-600 mb-4">
                Enter the email address where you'd like to receive a test copy of this notification.
              </p>

              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  autoFocus
                />
              </div>

              {testEmailResult && (
                <div className={`mb-4 p-3 rounded-md text-sm ${
                  testEmailResult.success
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}>
                  {testEmailResult.message}
                </div>
              )}

              <div className="flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowTestEmailModal(false);
                    setTestEmailResult(null);
                  }}
                  disabled={sendingTest}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSendTest}
                  disabled={sendingTest || !testEmail}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {sendingTest ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending...
                    </>
                  ) : (
                    'Send Test'
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Skip Modal */}
      {showSkipModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold mb-4">Skip Notification</h3>
              <p className="text-sm text-slate-600 mb-4">
                This notification will not be sent. You can re-enable it later if the scheduled date hasn't passed.
              </p>

              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Reason (optional)
                </label>
                <textarea
                  value={skipReason}
                  onChange={(e) => setSkipReason(e.target.value)}
                  placeholder="Why is this notification being skipped?"
                  rows={3}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowSkipModal(false);
                    setSkipReason('');
                  }}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSkip}
                  disabled={saving}
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  {saving ? 'Skipping...' : 'Skip Notification'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScheduledEmailEdit;
